/**
 * Logger Factory
 *
 * Provides easy access to logging functionality throughout the application.
 * Creates and manages a global logging service instance with sensible defaults.
 *
 * @module services/shared
 */

import { LogLevel } from './ILogger';
import type { ILogger, LoggerConfig } from './ILogger';
import { ConsoleLogger } from './ConsoleLogger';
import { LoggingService } from './LoggingService';
import type { LoggingServiceConfig } from './LoggingService';

/**
 * Global logging service instance
 */
let globalLoggingService: LoggingService | null = null;

/**
 * Default logger configuration for the application
 */
const DEFAULT_LOGGER_CONFIG: LoggerConfig = {
  level: import.meta.env.PROD ? LogLevel.LOG : LogLevel.DEBUG,
  includeTimestamp: true,
  prefix: '[BiteWise]',
  enabled: true
};

/**
 * Default logging service configuration
 */
const DEFAULT_SERVICE_CONFIG: LoggingServiceConfig = {
  defaultLevel: import.meta.env.PROD ? LogLevel.LOG : LogLevel.DEBUG,
  enabled: true,
  maxLoggers: 5
};

/**
 * Initialize the global logging service with default configuration
 */
function initializeLoggingService(): LoggingService {
  if (globalLoggingService) {
    return globalLoggingService;
  }

  // Create the logging service
  globalLoggingService = new LoggingService(DEFAULT_SERVICE_CONFIG);

  // Add default console logger
  const consoleLogger = new ConsoleLogger(DEFAULT_LOGGER_CONFIG);
  globalLoggingService.addLogger('console', consoleLogger);

  return globalLoggingService;
}

/**
 * Get the global logging service instance
 * Creates one if it doesn't exist
 */
export function getLoggingService(): LoggingService {
  return globalLoggingService || initializeLoggingService();
}

/**
 * Create a new logger instance with custom configuration
 * @param config - Logger configuration
 * @returns New logger instance
 */
export function createLogger(config: LoggerConfig = {}): ILogger {
  const mergedConfig = { ...DEFAULT_LOGGER_CONFIG, ...config };
  return new ConsoleLogger(mergedConfig);
}

/**
 * Create a new logging service with custom configuration
 * @param config - Service configuration
 * @returns New logging service instance
 */
export function createLoggingService(config: LoggingServiceConfig = {}): LoggingService {
  const mergedConfig = { ...DEFAULT_SERVICE_CONFIG, ...config };
  return new LoggingService(mergedConfig);
}

/**
 * Add a logger to the global logging service
 * @param name - Unique name for the logger
 * @param logger - Logger instance or configuration for ConsoleLogger
 * @returns true if logger was added successfully
 */
export function addLogger(name: string, logger: ILogger | LoggerConfig): boolean {
  const service = getLoggingService();

  if ('debug' in logger && 'log' in logger && 'warn' in logger && 'error' in logger) {
    // It's a logger instance
    return service.addLogger(name, logger);
  } else {
    // It's a configuration object, create a ConsoleLogger
    const consoleLogger = new ConsoleLogger(logger as LoggerConfig);
    return service.addLogger(name, consoleLogger);
  }
}

/**
 * Remove a logger from the global logging service
 * @param name - Name of the logger to remove
 * @returns true if logger was removed
 */
export function removeLogger(name: string): boolean {
  const service = getLoggingService();
  return service.removeLogger(name);
}

/**
 * Configure the global logging service
 * @param config - Service configuration
 */
export function configureLogging(config: LoggingServiceConfig): void {
  const service = getLoggingService();
  service.updateConfig(config);
}

/**
 * Enable or disable global logging
 * @param enabled - Whether to enable logging
 */
export function setLoggingEnabled(enabled: boolean): void {
  const service = getLoggingService();
  service.setEnabled(enabled);
}

/**
 * Reset the global logging service (useful for testing)
 */
export function resetLoggingService(): void {
  globalLoggingService = null;
}

/**
 * Get a logger instance for easy access
 * This is the main function that should be used throughout the application
 * @returns Logger interface with debug, log, warn, error methods
 */
export function getLogger(): ILogger {
  const service = getLoggingService();

  return {
    debug: (message: string, ...args: unknown[]) => service.debug(message, ...args),
    log: (message: string, ...args: unknown[]) => service.log(message, ...args),
    warn: (message: string, ...args: unknown[]) => service.warn(message, ...args),
    error: (message: string, ...args: unknown[]) => service.error(message, ...args)
  };
}

/**
 * Convenience function to get a logger with a specific prefix
 * @param prefix - Prefix for log messages
 * @returns Logger interface with the specified prefix
 */
export function getLoggerWithPrefix(prefix: string): ILogger {
  const config: LoggerConfig = {
    ...DEFAULT_LOGGER_CONFIG,
    prefix: `[BiteWise:${prefix}]`
  };

  return createLogger(config);
}

// Export the main logger instance for direct use
export const logger = getLogger();

// Export commonly used log levels
export { LogLevel };